import { useEffect, useState } from 'react';
import { NotionButton } from '../ui/notion';
import { useApp } from '../AppProvider';
import { supabase } from '../../lib/supabase';
import { friendlyDbError } from '../../lib/utils/friendlyDbError';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { cleanAvailability, type AvailabilityBlock, type OrgAvailability } from '../../lib/calendar/team/workHours';
import { BlocksEditor } from '../settings/calendar/WeekHoursEditor';

interface Props { personId: string; personName: string; canEdit: boolean }

/**
 * Le pause settimanali della persona (le stesse di Impostazioni › Calendario › Orari), modificabili dal tab
 * "Non disponibile". Si salvano con la funzione set_calendar_person_blocks: tocca solo le pause di quella persona
 * e la lascia usare a lei stessa o a chi ha "Segna le indisponibilità dei colleghi".
 */
export function PersonPausesEditor({ personId, personName, canEdit }: Props) {
  const { toast } = useApp();
  const saved = useSettingsStore(s => ((s.settings as any)?.calendar_settings?.availability as OrgAvailability | undefined)?.people?.[personId]?.blocks) || [];
  const savedKey = JSON.stringify(saved);
  const [draft, setDraft] = useState<AvailabilityBlock[]>(saved);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  // Cambiando persona (o se le pause cambiano altrove) si riparte da quelle salvate
  useEffect(() => { setDraft(JSON.parse(savedKey)); setDirty(false); }, [personId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!dirty) setDraft(JSON.parse(savedKey)); }, [savedKey, dirty]);

  const onSave = async () => {
    if (draft.some(b => b.end <= b.start || !b.days.length)) { toast.warning('Controlla le pause segnate in rosso'); return; }
    const blocks = cleanAvailability({ people: { [personId]: { blocks: draft } } }).people?.[personId]?.blocks || [];
    setSaving(true);
    const { data, error } = await supabase.rpc('set_calendar_person_blocks', { p_user_id: personId, p_blocks: blocks });
    setSaving(false);
    if (error) { toast.error(friendlyDbError(error)); return; }
    // La vista Team e l'avviso del modulo leggono le pause dalle impostazioni gia' caricate
    useSettingsStore.setState(s => (s.settings ? { settings: { ...s.settings, calendar_settings: { ...((s.settings as any).calendar_settings || {}), availability: data } } as any } : {}));
    setDirty(false);
    toast.success('Pause salvate');
  };

  return (
    <div className="mt-5">
      <div className="px-1.5 text-[11px] font-medium uppercase tracking-wide text-[rgba(55,53,47,0.5)] dark:text-gray-400">Pause ogni settimana di {personName}</div>
      <p className="px-1.5 mt-0.5 mb-1 text-[12px] text-[#787774]">Si ripetono tutte le settimane, come in Impostazioni › Calendario › Orari.</p>
      <fieldset disabled={!canEdit} className="px-1.5">
        <BlocksEditor blocks={draft} onChange={b => { setDraft(b); setDirty(true); }} />
      </fieldset>
      {dirty && canEdit && (
        <div className="mt-2 flex items-center justify-end gap-2 px-1.5">
          <NotionButton type="button" variant="ghost" size="sm" onClick={() => { setDraft(JSON.parse(savedKey)); setDirty(false); }}>Annulla modifiche</NotionButton>
          <NotionButton type="button" variant="primary" size="sm" loading={saving} onClick={() => void onSave()}>Salva pause</NotionButton>
        </div>
      )}
    </div>
  );
}
